import { registerLocaleData } from '@angular/common';
import ptBr from '@angular/common/locales/pt';
import { Component, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth.service';
import { CutoffConfig } from '../../models/cutoff-config';
import { CutoffConfigForm } from '../../models/cutoff-config-form';
import { HeaderTitle } from '../../models/generic';
import { SnackbarType } from '../../models/snackbar-type.enum';
import { ConfigurationNfService } from '../../services/configuracao-nf.service';
import { DataService } from '../../services/data.service';
import { SnackbarService } from '../../services/snackbar.service';

@Component({
  selector: 'app-invoice-cutoff-config',
  templateUrl: './configuracao-nf.component.html',
  styleUrls: ['./configuracao-nf.component.scss'],
  providers: [
    { provide: LOCALE_ID, useValue: 'pt-BR' }
  ]
})
export class ConfiguracaoNfComponent implements OnInit, OnDestroy {
  searchTerm = '';
  searchChanged$ = new Subject<string>();
  destroyed$ = new Subject<void>();

  currentPage = 1;
  rowsPerPageOptions = [5, 10, 20, 50];
  rowsPerPage = this.rowsPerPageOptions[0];

  sortColumn = '';
  sortDirection: 'asc' | 'desc' | '' = '';

  selectedConfig: CutoffConfig | null = null;
  showDeleteModal = false;
  showEditModal = false;

  availableMonthYears = this.generateMonthYearOptions();
  form: CutoffConfigForm = { monthYear: '', cutoffDate: '', cutoffTime: '' };
  formErrors: { [key: string]: string } = {};
  modalErrorMessage = '';

  configs: CutoffConfig[] = [];
  totalItems = 0;

  addedMonthYearOption: string | null = null;
  todayString: string = '';

  constructor(
    private activatedRoute: ActivatedRoute,
    private dataService: DataService,
    public authService: AuthService,
    private configService: ConfigurationNfService,
    private snackbarService: SnackbarService,
  ) {
    registerLocaleData(ptBr);
    const title = activatedRoute.snapshot.routeConfig?.title as HeaderTitle;
    this.dataService.setHeaderTitle(title);
  }

  ngOnInit(): void {
    const today = new Date();
    this.todayString = today.toISOString().split('T')[0]; // "yyyy-MM-dd"

    this.loadConfigs();

    this.searchChanged$
      .pipe(debounceTime(500), takeUntil(this.destroyed$))
      .subscribe(term => {
        this.searchTerm = term;
        this.currentPage = 1;
        this.loadConfigs();
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  loadConfigs(): void {
    const params = {
      search: this.searchTerm,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection,
      page: this.currentPage,
      rowsPerPage: this.rowsPerPage
    };

    this.configService.getAll(params).subscribe({
      next: response => {
        this.configs = response.items;
        this.totalItems = response.total;
      },
      error: err => {
        this.showError(err, 'Erro ao carregar configurações.');
      }
    });
  }

  get totalPages(): number {
    return Math.max(Math.ceil(this.totalItems / this.rowsPerPage), 1);
  }

  getSortValue(item: CutoffConfig): any {
    switch (this.sortColumn) {
      case 'monthYear': return `${item.month.toString().padStart(2, '0')}/${item.year}`;
      case 'limitDate': return item.limitDate;
      case 'limitTime': return item.limitTime;
      default: return null;
    }
  }

  sortBy(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : this.sortDirection === 'desc' ? '' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.currentPage = 1;
    this.loadConfigs();
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) return 'url(assets/icons/sort-null-icon.svg)';
    return this.sortDirection === 'asc'
      ? 'url(assets/icons/sort-up-icon.svg)'
      : this.sortDirection === 'desc'
        ? 'url(assets/icons/sort-down-icon.svg)'
        : 'url(assets/icons/sort-null-icon.svg)';
  }

  onSearchChange(term: string): void {
    this.searchChanged$.next(term);
  }

  onRowsPerPageChange(value: number): void {
    this.rowsPerPage = Number(value);
    this.currentPage = 1;
    this.loadConfigs();
  }

  goToPage(page: number): void {
    if (page !== this.currentPage && page >= 1) {
      this.currentPage = page;
      this.loadConfigs();
    }
  }

  openCreateModal(): void {
    this.selectedConfig = null;
    this.form = { monthYear: '', cutoffDate: '', cutoffTime: '' };
    this.formErrors = {};
    this.modalErrorMessage = '';
    this.showEditModal = true;
  }

  openEditModal(config: CutoffConfig): void {
    const monthYear = `${config.month.toString().padStart(2, '0')}/${config.year}`;

    this.selectedConfig = config;
    this.form = {
      id: config.id,
      monthYear: monthYear,
      cutoffDate: new Date(config.limitDate).toISOString().split('T')[0],
      cutoffTime: config.limitTime
    };

    if (!this.availableMonthYears.includes(monthYear)) {
      this.availableMonthYears.unshift(monthYear);
      this.addedMonthYearOption = monthYear;
    }

    this.formErrors = {};
    this.modalErrorMessage = '';
    this.showEditModal = true;
  }

  saveConfig(): void {
    this.modalErrorMessage = '';

    if (!this.validateForm()) {
      return;
    }

    const { monthYear, cutoffDate, cutoffTime, id } = this.form;
    const [monthStr, yearStr] = monthYear.split('/');
    const month = Number(monthStr);
    const year = Number(yearStr);

    const payload = {
      month,
      year,
      limitDate: new Date(cutoffDate),
      limitTime: cutoffTime
    };

    const request = id
      ? this.configService.update(id, payload)
      : this.configService.create(payload);

    request.subscribe({
      next: () => {
        this.loadConfigs();
        this.closeEditModal();
      },
      error: (err) => {
        this.showError(err, 'Erro ao salvar configuração.');
      }
    });
  }

  confirmRemove(config: CutoffConfig): void {
    this.selectedConfig = config;
    this.showDeleteModal = true;
  }

  executeDelete(): void {
    if (this.selectedConfig) {
      this.configService.delete(this.selectedConfig.id).subscribe({
        next: () => {
          this.currentPage = 1;
          this.loadConfigs();
          this.snackbarService.openSnackBar('Configuração excluída com sucesso!', SnackbarType.Success, 3000);
        },
        error: (err) => {
          this.showError(err, 'Erro ao excluir configuração.');
        }
      });
    }
    this.closeDeleteModal();
  }

  closeEditModal(): void {
    this.showEditModal = false;

    if (this.addedMonthYearOption) {
      this.availableMonthYears = this.availableMonthYears.filter(opt => opt !== this.addedMonthYearOption);
      this.addedMonthYearOption = null;
    }
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
  }

  private generateMonthYearOptions(): string[] {
    const now = new Date();
    return Array.from({ length: 12 }, (_, i) => {
      const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      return `${month}/${date.getFullYear()}`;
    });
  }

  private showError(err: any, fallback: string): void {
    const message = err?.error?.Message || err?.error?.message || fallback;
    this.modalErrorMessage = message;
    this.snackbarService.openSnackBar(message, SnackbarType.Danger, 5000);

    setTimeout(function () {
      document.querySelector('.loader')?.remove();
    }, 1000);
  }

  validateForm(): boolean {
    this.formErrors = {};

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Converte a string 'yyyy-MM-dd' para uma data local (evita problemas com fuso horário UTC)
    const parts = this.form.cutoffDate.split('-');
    const selectedDate = new Date(
      Number(parts[0]),        // Year
      Number(parts[1]) - 1,    // Month (base 0)
      Number(parts[2])         // Day
    );
    selectedDate.setHours(0, 0, 0, 0);

    const selectedTimeStr = this.form.cutoffTime;

    if (!this.form.monthYear) {
      this.formErrors['monthYear'] = 'O campo Mês/Ano é obrigatório.';
    }

    if (!this.form.cutoffDate) {
      this.formErrors['cutoffDate'] = 'A Data Limite é obrigatória.';
    } else if (selectedDate < today) {
      let wasModified = true;

      if (this.selectedConfig?.limitDate) {
        const originalDate = new Date(this.selectedConfig.limitDate);
        originalDate.setHours(0, 0, 0, 0);

        // Verifica se o usuário alterou a data em relação à original
        wasModified =
          selectedDate.getFullYear() !== originalDate.getFullYear() ||
          selectedDate.getMonth() !== originalDate.getMonth() ||
          selectedDate.getDate() !== originalDate.getDate();
      }

      if (wasModified) {
        this.formErrors['cutoffDate'] = 'A Data Limite não pode ser no passado.';
      }
    }

    if (!selectedTimeStr) {
      this.formErrors['cutoffTime'] = 'A Hora Limite é obrigatória.';
    } else if (selectedDate.getTime() === today.getTime()) {
      const now = new Date();
      const [hours, minutes] = selectedTimeStr.split(':');
      const selectedTime = new Date();
      selectedTime.setHours(+hours, +minutes, 0, 0);

      if (selectedTime < now) {
        this.formErrors['cutoffTime'] = 'A Hora Limite não pode ser menor que a hora atual.';
      }
    }

    if (Object.keys(this.formErrors).length > 0) {
      this.showError(null, 'Por favor, preencha todos os campos corretamente.');
      return false;
    }

    return true;
  }
}
